/**
 * Pure helper functions: merge Product + ReadinessScore + PackshotStatus
 * Plan 012: Products + Readiness Merge
 */

import type { Product, ProductWithReadiness, PackshotType } from '@/types/products'
import type { ReadinessScore } from '@/lib/api/channel-readiness'
import type { ProductPackshotStatus } from '@/lib/api/image-intelligence'
import type { AIRefSummaryEntry } from '@/lib/api/products'

export function buildReadinessMap(scores: ReadinessScore[]): Map<string, ReadinessScore> {
  return new Map(scores.map(s => [s.product_id, s]))
}

export function mergeProduct(
  product: Product,
  readinessMap: Map<string, ReadinessScore>,
  packshotMap: Map<string, ProductPackshotStatus> | undefined,
  aiRefMap?: Map<string, AIRefSummaryEntry>,
): ProductWithReadiness {
  const score = readinessMap.get(product.id)
  const ps = packshotMap?.get(product.id)
  const best = ps?.best_candidate
  const aiRef = aiRefMap?.get(product.id)
  return {
    ...product,
    readiness_action_state: score ? (score.issue_count === 0 ? 'ready' : 'action_required') : undefined,
    readiness_score: score?.readiness_score,
    issues_count: score?.issue_count,
    packshot_score: best ? Math.round(best.overall_score) : undefined,
    packshot_type: best?.category as PackshotType | undefined,
    packshot_assigned: ps?.has_packshot_assigned,
    packshot_thumbnail_url: best?.thumbnail_url,
    packshot_status: ps?.status,
    has_ai_ref_packshot: aiRef?.has_packshot_ref ?? false,
    ai_ref_packshot_thumbnail_url: aiRef?.packshot_thumbnail_url ?? null,
  }
}

export function mergeFromScore(
  score: ReadinessScore,
  packshotMap: Map<string, ProductPackshotStatus> | undefined,
  aiRefMap?: Map<string, AIRefSummaryEntry>,
): ProductWithReadiness {
  const p = score.product
  const ps = packshotMap?.get(score.product_id)
  const best = ps?.best_candidate
  const aiRef = aiRefMap?.get(score.product_id)
  return {
    id: score.product_id,
    workspace_id: '', workspace_integration_id: '', external_id: '',
    asin: null,
    sku: p?.sku ?? null, gtin: null,
    name: p?.name ?? score.product_id,
    description: null, short_description: null,
    image_url: p?.image_url ?? null, price_net: null, price_gross: null,
    is_active: true, raw_data: {}, synced_at: null, created_at: '', updated_at: '',
    first_image_url: p?.first_image_url ?? p?.image_url ?? null,
    quantity_30d: p?.quantity_30d ?? undefined, revenue_30d: p?.revenue_30d ?? undefined,
    quantity_90d: p?.quantity_90d ?? undefined, revenue_90d: p?.revenue_90d ?? undefined,
    readiness_action_state: score.issue_count === 0 ? 'ready' : 'action_required',
    readiness_score: score.readiness_score,
    issues_count: score.issue_count,
    packshot_score: best ? Math.round(best.overall_score) : undefined,
    packshot_type: best?.category as PackshotType | undefined,
    packshot_assigned: ps?.has_packshot_assigned,
    packshot_thumbnail_url: best?.thumbnail_url,
    packshot_status: ps?.status,
    has_ai_ref_packshot: aiRef?.has_packshot_ref ?? false,
    ai_ref_packshot_thumbnail_url: aiRef?.packshot_thumbnail_url ?? null,
  }
}
